import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { BulkCreateBenefitActivationsDTO, BulkResultItemDTO, CreateBenefitActivationDTO } from 'probonio-shared-ui/api';
import { apis } from 'probonio-shared-ui/module/api';
import { useTenantID } from 'probonio-shared-ui/module/me';
import { SetStateAction, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { chunkArray } from '../../../util/chunkArray';
import { ActivationDialogSaveFunctions } from '../ActivationDialog';
import { enqueueBulkResultSnackbar } from '../bulkActivation/bulkResultSnackbar';
import { useRefetchActivations } from '../query';

const BATCH_SIZE = 20;

export function useBulkActivationSaveFunctions(
  employeeIds: string[],
  setProgress: (value: SetStateAction<number | undefined>) => void,
  onSuccessReset: () => void,
): ActivationDialogSaveFunctions {
  const { t } = useTranslation('benefitsModule');
  const getTenantId = useTenantID();
  const refetchActivations = useRefetchActivations();
  const { enqueueSnackbar } = useSnackbar();

  const bulkActivateBenefitMutation = useMutation({
    mutationFn: (params: BulkCreateBenefitActivationsDTO) =>
      apis.benefits.createBenefitActivations({ tenantId: getTenantId(), bulkCreateBenefitActivationsDTO: params }).then(res => res.data),
  });

  const handleCreateActivation = useCallback(
    async (benefitActivation: CreateBenefitActivationDTO) => {
      const aggregated: BulkResultItemDTO[] = [];
      let caughtError: unknown;
      try {
        for (const chunk of chunkArray(employeeIds, BATCH_SIZE)) {
          const chunkResult = await bulkActivateBenefitMutation.mutateAsync({ employeeIds: chunk, benefitActivation });
          aggregated.push(...chunkResult.results);
          setProgress((aggregated.length / employeeIds.length) * 100);
        }
      } catch (err) {
        caughtError = err;
      }
      setProgress(undefined);

      if (aggregated.length > 0) {
        await refetchActivations();
        const successCount = enqueueBulkResultSnackbar(aggregated, t, enqueueSnackbar, {
          empty: 'bulkActivation.noNewActivationsDone',
          successFragment: 'bulkActivation.activatedCount',
        });

        if (successCount) {
          onSuccessReset();
        }
      }

      if (caughtError) {
        throw caughtError instanceof Error ? caughtError : new Error(String(caughtError));
      }
    },
    [bulkActivateBenefitMutation, employeeIds, enqueueSnackbar, onSuccessReset, refetchActivations, setProgress, t],
  );

  return useMemo(
    () => ({
      onCreateActivation: handleCreateActivation,
    }),
    [handleCreateActivation],
  );
}
